import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

/**
 * 레퍼런스 재생 — 화면이 열라고 할 때 **처음부터** 튼다.
 *
 * 재생 시작 시각이 채점의 시간 기준(anchor)이다: 멜로디의 `startMs`는 음원 t=0 기준이고
 * 프레임의 `timestampMs`는 캡처 타임라인 기준이라, 둘을 잇는 건 "재생을 언제 걸었나"뿐이다
 * (songScoring 모듈 주석). 그래서 여기서는 `playing`이 켜지는 순간 **되감아서** 튼다 —
 * 이어 재생하면 앵커가 조용히 어긋난다. *언제* 켜는지는 부모가 정한다.
 *
 * 소리는 스피커로 나가고 마이크는 그걸 같이 듣는다. 에코 제거는 기기 몫이다 — 노래 테이크는
 * iOS VPIO(`setVoiceProcessingEnabled`, AEC)를 켜고, 세 측정 테이크는 raw 로 둔다
 * (2026-09-16, `MuzlyAudioCapture.mm spinUpEngine` 주석). 잔여 누설은 채점이 안고 돈다
 * (songScoring 한계 주석).
 *
 * **WebAudio 게인은 쓰지 않는다** (2026-09-16 제거). 네이티브 셸에서 출력에 4배 게인을 걸던
 * 우회가 있었는데, 그것은 캡처 세션이 `.measurement`로 서서 출력이 깎이던 시절의 처방이다.
 * 노래 테이크는 지금 `.default`라 출력이 안 깎이고, 4배는 스피커만 키워 마이크로 더 크게
 * 되돌아왔다 — 실기기에서 입력 rms가 -18.5dBFS까지 올라 음성 게이트가 방을 아예 못 쟀다
 * (`floor=OPEN`, `oct=114`). 엘리먼트를 `AudioContext`에 물리면 iOS가 그 그래프를 재울 때
 * 반주가 통째로 사라지는 문제도 같이 있었다(2026-09-10).
 */

interface SongReferenceAudioProps {
  /** 미리 받아 둔 음원(objectURL) — 여기서 처음 네트워크를 타지 않는다. */
  src: string;
  playing: boolean;
  /**
   * 재생 위치를 밖에서 **읽는** 통로(`BackingWaveform`). 재생 제어는 여전히 이 컴포넌트만
   * 한다 — 읽는 쪽이 `currentTime`을 쓰거나 `play()`를 부르면 채점 앵커가 흔들린다.
   */
  audioRef?: RefObject<HTMLAudioElement | null>;
  /**
   * `play()`가 거절당했다는 것을 위로 올린다. 녹음은 그대로 가지만(아래 참조) **말은 해야
   * 한다** — 반주가 안 들린 테이크는 채점이 성립하지 않는데, 이 자리는 2026-09-18 실기기
   * 보고("반주가 같이 안 나온다")를 코드만 읽어서는 가를 수 없었던 유일한 지점이었다.
   * 거절 사유가 통째로 삼켜져 앱의 어떤 신호에도 안 남았기 때문이다.
   */
  onPlayFailed?: (error: unknown) => void;
}

/** 한 테이크에서 저절로 멈춘 재생을 되살리는 횟수의 상한. */
const MAX_RESUMES = 3;

export const SongReferenceAudio = ({
  src,
  playing,
  audioRef,
  onPlayFailed,
}: SongReferenceAudioProps) => {
  const ownRef = useRef<HTMLAudioElement>(null);
  const ref = audioRef ?? ownRef;

  useEffect(() => {
    const audio = ref.current;
    if (!audio) {
      return;
    }
    if (!playing) {
      audio.pause();
      return;
    }
    audio.currentTime = 0;
    // 사용자가 "시작"을 누른 흐름 안이라 자동재생 정책에 걸리지 않는다. 그래도 실패할 수
    // 있는 경로(오디오 포커스 상실 등)라 **던지지는** 않는다 — 화면을 멈춰 세우면 이미
    // 잰 두 진단까지 잃는다. 다만 조용히 넘기지도 않는다: 거절을 삼키면 "반주가 안 나온다"가
    // 앱 어디에도 안 남아, 실기기에서만 나는 증상을 코드로 가를 수 없다.
    const report = (error: unknown): void => onPlayFailed?.(error);
    void audio.play().catch(report);

    // 우리가 멈추지 않았는데 멈춘 재생은 이어서 튼다. 2026-09-21 실기기(재측정): 재생이
    // 시작되고 0.11초 뒤 엘리먼트가 멈췄는데 인터럽션도 `play()` 거절도 아니어서
    // (`AudioQueue BeginPause: beingInterrupted 0`) 위 catch 에는 아무것도 안 걸렸고, 테이크는
    // 반주 없이 끝까지 갔다. 되감지는 않는다 — 채점 앵커는 처음 튼 시각이다. 횟수를 막아 두는
    // 것은 계속 멈추는 기기에서 play/pause 가 맞물려 도는 것을 끊기 위해서다.
    let resumes = 0;
    const onPause = (): void => {
      if (audio.ended || resumes >= MAX_RESUMES) {
        return;
      }
      resumes += 1;
      const error = new Error(`paused at ${audio.currentTime.toFixed(2)}s`);
      error.name = 'PausedWhilePlaying';
      report(error);
      void audio.play().catch(report);
    };
    audio.addEventListener('pause', onPause);
    return () => audio.removeEventListener('pause', onPause);
  }, [playing, src, ref, onPlayFailed]);

  return <audio ref={ref} src={src} preload="auto" aria-hidden="true" />;
};
